import { Cpu, ShieldCheck, Zap } from "lucide-react";
import * as React from "react";

import type { TileSummary } from "@/api/tiles";
import { cardVariants } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Catalog picker — selectable card per Tile manifest.
 *
 * Each card surfaces the high-signal fields a scientist scans before
 * picking a Tile: the dotted-namespace name, the version, a brief
 * description, security level, and whether the Tile requires GPU.
 *
 * Selection is single-row; click a card to make it active. The host
 * page consumes ``selected`` to wire up the ManifestForm.
 *
 * Per Rupert PR-260: the radiogroup ARIA contract demands roving
 * tabindex + arrow-key navigation. Cards now use ↑↓←→ to move
 * between options and Home/End to jump to the ends; tabindex
 * follows the active card (or the selected one if there is one).
 */
export interface TilePickerProps {
  tiles: TileSummary[] | undefined;
  selected: string | null;
  onSelect: (name: string) => void;
  isLoading?: boolean;
  className?: string;
}

export function TilePicker({
  tiles,
  selected,
  onSelect,
  isLoading,
  className,
}: TilePickerProps): JSX.Element {
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  if (isLoading || !tiles) {
    return (
      <div className={cn("grid gap-3 sm:grid-cols-2", className)}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
    );
  }

  if (tiles.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border-subtle bg-surface-2/50 p-8 text-center text-sm text-muted-foreground">
        No Tile manifests found in the configured tiles directory.
      </div>
    );
  }

  const selectedIdx = tiles.findIndex((t) => t.name === selected);
  // Roving tabindex: when no Tile is selected, only the first card is
  // tabbable so Tab lands in the group; otherwise the selected card.
  const focusIdx = selectedIdx === -1 ? 0 : selectedIdx;

  const moveFocus = (target: number): void => {
    const buttons =
      containerRef.current?.querySelectorAll<HTMLButtonElement>("button[role='radio']");
    if (!buttons || buttons.length === 0) return;
    const wrapped = ((target % buttons.length) + buttons.length) % buttons.length;
    buttons[wrapped]?.focus();
    onSelect(tiles[wrapped].name);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number): void => {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        moveFocus(idx + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        moveFocus(idx - 1);
        break;
      case "Home":
        e.preventDefault();
        moveFocus(0);
        break;
      case "End":
        e.preventDefault();
        moveFocus(tiles.length - 1);
        break;
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn("grid gap-3 sm:grid-cols-2", className)}
      role="radiogroup"
    >
      {tiles.map((tile, idx) => {
        const isSelected = selected === tile.name;
        return (
          <button
            key={tile.name}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={idx === focusIdx ? 0 : -1}
            onClick={() => onSelect(tile.name)}
            onKeyDown={(e) => onKeyDown(e, idx)}
            // The recipe as a class string, not `<Card>`: the element is a
            // `<button role="radio">` and a card-shaped control keeps its own
            // focus and hover treatment, which is why `CardElement` excludes
            // `button`. See `cardVariants`' own comment.
            className={cn(
              cardVariants(),
              "group flex flex-col gap-2 p-card text-left transition-colors",
              isSelected
                ? "border-cyan/60 bg-surface-2 shadow-[inset_2px_0_0_hsl(var(--ring))]"
                : "hover:border-border-strong hover:bg-surface-2"
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-mono text-sm font-medium text-foreground">{tile.name}</h3>
              <span className="rounded-sm bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                v{tile.version}
              </span>
            </div>
            {tile.description && (
              <p className="line-clamp-2 text-xs text-muted-foreground">{tile.description}</p>
            )}
            <div className="mt-auto flex flex-wrap gap-1.5 text-[10px]">
              <span className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-surface-2 px-1.5 py-0.5 text-muted-foreground">
                <ShieldCheck className="h-3 w-3" aria-hidden />
                sec L{tile.minimum_security_level}
              </span>
              {tile.requires_gpu ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-state-running-border bg-state-running-bg px-1.5 py-0.5 text-state-running-fg">
                  <Zap className="h-3 w-3" aria-hidden />
                  GPU
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-surface-2 px-1.5 py-0.5 text-muted-foreground">
                  <Cpu className="h-3 w-3" aria-hidden />
                  CPU
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
